import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { BulletList } from '@tiptap/extension-bullet-list';
import { OrderedList } from '@tiptap/extension-ordered-list';
import Link from '@tiptap/extension-link';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import TextAlign from '@tiptap/extension-text-align';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import { FillableFieldMark, FillableFieldPlaceholder } from './tipTapFillableFieldExtension';
import { SignatureBlock } from './tipTapSignatureBlockExtension';
import { PaymentButton } from './tipTapPaymentButtonExtension';
import { FormBlock } from './tipTapFormBlockExtension';

// Same extension set as readEditorContent.js (minus React node views)
// so headless HTML serialization matches the read-only renderer.
const extensions = [
  StarterKit.configure({
    bulletList: false,
    orderedList: false,
    heading: { levels: [1, 2, 3, 4] },
    history: false,
    link: false,
  }),
  BulletList.extend({
    addAttributes() {
      return {
        ...this.parent?.(),
        indent: {
          default: 0,
          parseHTML: element => parseInt(element.getAttribute('data-indent') || '0', 10),
          renderHTML: attributes => {
            if (!attributes.indent) return {};
            return {
              'data-indent': attributes.indent,
              style: `margin-left: ${attributes.indent * 2}em`,
            };
          },
        },
      };
    },
  }).configure({ keepMarks: true }),
  OrderedList.extend({
    addAttributes() {
      return {
        ...this.parent?.(),
        indent: {
          default: 0,
          parseHTML: element => parseInt(element.getAttribute('data-indent') || '0', 10),
          renderHTML: attributes => {
            if (!attributes.indent) return {};
            return {
              'data-indent': attributes.indent,
              style: `margin-left: ${attributes.indent * 2}em`,
            };
          },
        },
      };
    },
  }).configure({ keepMarks: true }),
  Image.configure({
    inline: false,
    allowBase64: true,
    HTMLAttributes: { class: 'rounded-lg my-3 max-w-full h-auto' },
  }),
  Link.configure({
    openOnClick: true,
    autolink: true,
    protocols: ['mailto', 'http', 'https'],
    HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
  }),
  TaskList,
  TaskItem.configure({ nested: true }),
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
  Superscript,
  Subscript,
  Highlight,
  TextStyle,
  Color,
  Table.configure({
    resizable: true,
    HTMLAttributes: { class: 'my-table' },
  }),
  TableRow,
  TableHeader,
  TableCell,
  FillableFieldMark,
  FillableFieldPlaceholder,
  SignatureBlock,
  PaymentButton,
  FormBlock,
];

/**
 * Convert TipTap editor JSON to an HTML string using a headless editor instance.
 * Used when editorHtml is unavailable (e.g. encrypted document snapshots).
 */
export function editorJsonToHtml(json) {
  if (!json) return '';
  const parsed = typeof json === 'string' ? JSON.parse(json) : json;
  const el = document.createElement('div');
  const editor = new Editor({
    element: el,
    extensions,
    content: parsed,
    editable: false,
  });
  const html = editor.getHTML();
  editor.destroy();
  return html;
}
